import React, { useEffect, useMemo, useState } from "react";
import { listOtherProfiles, MAX_GROUP_MEMBERS } from "./chatService";
import { avatarColor, avatarInitial } from "./avatar";

const CREATE_TIMEOUT_MS = 12000;

function withCreateTimeout(promise, timeoutMs, message) {
  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(message)), timeoutMs);
  });
  return Promise.race([promise, timeoutPromise]).finally(() => clearTimeout(timeoutId));
}

export default function NewChatModal({
  meUid,
  onClose,
  onCreate,
  mode = "new",
  existingMemberIds = [],
  title = "New chat",
  description,
  submitLabel,
  defaultGroupName = "",
}) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [picked, setPicked] = useState(new Set());
  const [groupName, setGroupName] = useState(defaultGroupName || "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const addMode = mode === "add-people";

  useEffect(() => {
    setGroupName(defaultGroupName || "");
  }, [defaultGroupName]);

  useEffect(() => {
    (async () => {
      try {
        setUsers(await listOtherProfiles(meUid));
      } catch (e) {
        setError(e.message || "couldn't load users");
      } finally {
        setLoading(false);
      }
    })();
  }, [meUid]);

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const eligible = users.filter((u) => !existingMemberIds.includes(u.uid));
    if (!q) return eligible;
    return eligible.filter((u) => (u.displayName || "").toLowerCase().includes(q));
  }, [existingMemberIds, users, filter]);

  function toggle(uid) {
    const next = new Set(picked);
    if (next.has(uid)) next.delete(uid); else next.add(uid);
    setPicked(next);
  }

  const isGroup = addMode || picked.size > 1;
  const totalMembers = addMode ? existingMemberIds.length + picked.size : picked.size + 1;
  const tooMany = totalMembers > MAX_GROUP_MEMBERS;

  async function submit() {
    if (picked.size === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      if (addMode) {
        await withCreateTimeout(
          onCreate({
            type: "add-people",
            name: groupName.trim() || defaultGroupName || "Untitled group",
            memberIds: Array.from(picked),
          }),
          CREATE_TIMEOUT_MS,
          "Creating the chat is taking too long. Firestore may be throttled right now; please try again.",
        );
      } else if (isGroup) {
        const auto = users.filter((u) => picked.has(u.uid)).map((u) => u.displayName);
        const name = groupName.trim() || (auto.length <= 3 ? auto.join(", ") : auto.slice(0, 2).join(", ") + " +" + (auto.length - 2));
        await withCreateTimeout(
          onCreate({ type: "group", name, memberIds: Array.from(picked) }),
          CREATE_TIMEOUT_MS,
          "Creating the chat is taking too long. Firestore may be throttled right now; please try again.",
        );
      } else {
        const otherUid = Array.from(picked)[0];
        await withCreateTimeout(
          onCreate({ type: "dm", otherUid }),
          CREATE_TIMEOUT_MS,
          "Creating the chat is taking too long. Firestore may be throttled right now; please try again.",
        );
      }
    } catch (e) {
      setError(e.message || "couldn't create");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="chat-modal-bg" onClick={onClose}>
      <div className="chat-modal" onClick={(e) => e.stopPropagation()}>
        <h2>{title}</h2>
        <p className="chat-muted">
          {description || `Pick one person for a DM, or up to ${MAX_GROUP_MEMBERS - 1} for a group chat.`}
        </p>

        <input
          autoFocus
          placeholder="Search by name"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="chat-modal-search"
        />

        <div className="chat-modal-userlist">
          {loading ? (
            <div className="chat-muted">loading…</div>
          ) : filtered.length === 0 && users.filter((u) => !existingMemberIds.includes(u.uid)).length === 0 ? (
            <div className="chat-muted">No more people available for this chat.</div>
          ) : users.length === 0 ? (
            <div className="chat-muted">
              No other users yet. Sign up another account in another browser to test.
            </div>
          ) : filtered.length === 0 ? (
            <div className="chat-muted">No users match.</div>
          ) : (
            filtered.map((u) => (
              <label
                key={u.uid}
                className={"chat-user-row" + (picked.has(u.uid) ? " picked" : "")}
              >
                <input
                  type="checkbox"
                  checked={picked.has(u.uid)}
                  onChange={() => toggle(u.uid)}
                />
                <div
                  className="chat-avatar sm"
                  style={{ background: avatarColor(u.uid) }}
                  aria-hidden="true"
                >
                  {avatarInitial(u.displayName)}
                </div>
                <span className="chat-user-name">{u.displayName}</span>
              </label>
            ))
          )}
        </div>

        {(isGroup || addMode) && (
          <input
            placeholder="Group name (optional)"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            maxLength={60}
            className="chat-modal-search"
            style={{ marginTop: 10 }}
          />
        )}

        {tooMany && (
          <div className="chat-auth-error">
            Groups are capped at {MAX_GROUP_MEMBERS} members.
          </div>
        )}
        {error && <div className="chat-auth-error">{error}</div>}

        <div className="chat-modal-footer">
          <button className="chat-btn-link" onClick={onClose}>Cancel</button>
          <button
            className="chat-btn-primary"
            onClick={submit}
            disabled={picked.size === 0 || tooMany || submitting}
          >
            {submitting
              ? "…"
              : submitLabel || (isGroup ? `Create group (${totalMembers})` : "Start DM")}
          </button>
        </div>
      </div>
    </div>
  );
}
